import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { Subject, switchMap, take, takeUntil } from 'rxjs';

import { collapse, filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  DialogService,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  PlaceloaderComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { HttpErrorHandlerService } from '../../common';
import { ErrorMessagesWrapperComponent } from '../../misc/error-messages-wrapper/error-messages-wrapper.component';
import { FormConfigStoreService } from '../data/src/form-config/form-config.service';
import { FieldMappingFormFieldComponent } from '../field-mapping-form-field/field-mapping-form-field.component';
import type { FormConfig, FormInput, Source } from '../types';
import { FormConfigMode } from '../types';

@Component({
  selector: 'rti-field-mapping-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form.component.html',
  styleUrls: ['./field-mapping-form.component.less'],
  animations: [collapse],
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    CommonModule,
    DividerComponent,
    ErrorMessagesWrapperComponent,
    FieldMappingFormFieldComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    PlaceloaderComponent,
    SpinnerComponent,
    InfoboxComponent,
    TranslatePipe,
  ],
})
export class FieldMappingFormComponent {
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly formConfigStore = inject(FormConfigStoreService);
  private readonly dialog = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);

  public readonly integrationName = input<string>(undefined!);
  public readonly integrationKind = input<string>(undefined!);

  public readonly sourcesLoading = input<boolean>(true);
  public readonly sources = input<Source[]>([]);

  public readonly isEditing = input<boolean>(false);
  @Input() public formConfig: FormConfig;

  public formConfigDeleted = output<number>();

  public formConfigExpanded = output<number>();

  public formConfigClosed = output<void>();

  public isLoading: boolean = false;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isDynamic(): boolean {
    return this.formConfig.mode === FormConfigMode.DYNAMIC;
  }

  public trackByChecksum(_index: number, { checksum }: FormInput): string {
    return checksum;
  }

  public startEditing(): void {
    this.formConfigExpanded.emit(this.formConfig.id);
  }

  public stopEditing(): void {
    this.formConfigClosed.emit();
  }

  public delete(): void {
    this.confirm$()
      .pipe(
        take(1),
        filterBoolean,
        switchMap(() =>
          this.formConfigStore.deleteFormConfig(this.integrationKind(), this.formConfig.id),
        ),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe({
        next: () => {
          this.formConfigDeleted.emit(this.formConfig.id);
        },
        error: error => {
          this.httpErrorHandlerService.default(error);
        },
      });
  }

  public handleFormFieldChange(formInput: FormInput): void {
    this.formConfig.inputs = this.formConfig.inputs?.map(input =>
      input.checksum === formInput.checksum ? formInput : input,
    );

    this.cdRef.detectChanges();
  }

  public badgeLabel(): string {
    return `${this.mappedInputsCount()}/${this.formConfig.inputsCount}`;
  }

  private mappedInputsCount(): number {
    return (this.formConfig.inputs || <FormInput[]>[])
      .flatMap(this.inputs.bind(this))
      .filter((i: FormInput) => i.fieldMappings.length).length;
  }

  private inputs(input: FormInput): FormInput[] {
    return [input, ...(<FormInput[]>(input.inputs || []).flatMap(this.inputs.bind(this)))];
  }

  private confirm$(): Observable<boolean> {
    const title = this.i18n.translate('rti.field_mapping.settings.form_config.delete.dialog.title');
    const message = this.i18n.translate(
      'rti.field_mapping.settings.form_config.delete.dialog.message',
    );

    return this.dialog
      .error(
        {
          title,
          message,
        },
        this.vcRef,
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }
}
